// Word (.docx) "Case brief" export — a short, portable summary of the
// case suitable for emailing to an advisor, regulator, or counterparty.
//
// Contents:
//   1. Cover page (case name, status, date range, record count)
//   2. Table of contents (auto-generated from heading styles by Word)
//   3. Case summary (cached cases.summary)
//   4. Current status (deterministic status paragraph)
//   5. Key facts (cached cases.key_facts table)
//   6. Parties involved (contacts ∪ from/to addresses)
//   7. Chronological narrative (cached cases.story — prose)
//   8. Volume summaries (only if the case has more than one volume)
//   9. AI disclaimer
//
// Full records and figures are PDF-only (Full Dossier export). The
// brief is a standalone document that a regulator or attorney can
// read in 5-15 pages regardless of case size.

import {
  AlignmentType,
  Document,
  Footer,
  Header,
  HeadingLevel,
  PageBreak,
  PageNumber,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableOfContents,
  TableRow,
  TextRun,
  WidthType,
} from "docx";

import type { KeyFact } from "../types";
import { CASE_STATUS_LABELS } from "../types";
import {
  AI_DISCLAIMER,
  KEY_FACT_TYPE_LABEL,
  buildStatusParagraph,
  copyrightLine,
  dateRangeLine,
  extractParties,
  formatLong,
  parseKeyFacts,
  slugify,
  stripProseTimestamps,
  triggerDownload,
  volumeDateRange,
  type ExportBundle,
  type PartySummary,
  type VolumeBriefEntry,
} from "./exportShared";
import type { CaseRecord } from "../types";
import type { CaseOverview } from "./caseOverview";

const FONT = "Calibri";
const COLOR_MUTED = "666666";
const COLOR_HEADING = "1a1a1a";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function run(
  text: string,
  opts: Partial<{
    bold: boolean;
    italics: boolean;
    size: number;
    color: string;
  }> = {},
): TextRun {
  return new TextRun({
    text,
    font: FONT,
    bold: opts.bold,
    italics: opts.italics,
    size: opts.size ? opts.size * 2 : 22,
    color: opts.color,
  });
}

function para(
  text: string,
  opts: Partial<{
    bold: boolean;
    italics: boolean;
    size: number;
    color: string;
    alignment: (typeof AlignmentType)[keyof typeof AlignmentType];
    spacingAfter: number;
  }> = {},
): Paragraph {
  return new Paragraph({
    alignment: opts.alignment,
    spacing: { after: opts.spacingAfter ?? 120 },
    children: [
      run(text, {
        bold: opts.bold,
        italics: opts.italics,
        size: opts.size,
        color: opts.color,
      }),
    ],
  });
}

function heading1(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 360, after: 180 },
    children: [
      new TextRun({
        text,
        font: FONT,
        bold: true,
        size: 32,
        color: COLOR_HEADING,
      }),
    ],
  });
}

function heading2(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 120 },
    children: [
      new TextRun({
        text,
        font: FONT,
        bold: true,
        size: 26,
        color: COLOR_HEADING,
      }),
    ],
  });
}

function cell(
  content: string | Paragraph[],
  opts: Partial<{ bold: boolean; width: number; shaded: boolean }> = {},
): TableCell {
  const paras =
    typeof content === "string"
      ? [para(content, { bold: opts.bold, size: 10 })]
      : content;
  return new TableCell({
    width: opts.width
      ? { size: opts.width, type: WidthType.PERCENTAGE }
      : undefined,
    shading: opts.shaded ? { fill: "F5F2EC" } : undefined,
    children: paras,
  });
}

function prose(text: string, size = 11): Paragraph[] {
  const out: Paragraph[] = [];
  for (const line of text.split(/\r?\n/)) {
    out.push(
      new Paragraph({
        spacing: { after: 120 },
        children: [run(line || " ", { size })],
      }),
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function buildCover(
  caseName: string,
  status: string,
  rangeLine: string,
  recordCount: number,
): Paragraph[] {
  const children: Paragraph[] = [];
  for (let i = 0; i < 4; i++) children.push(new Paragraph({ children: [] }));
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 180 },
      children: [
        new TextRun({
          text: caseName,
          font: FONT,
          bold: true,
          size: 48,
          color: COLOR_HEADING,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [run("Case brief", { size: 14, color: COLOR_MUTED })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [run(status, { size: 12, color: COLOR_MUTED, bold: true })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [run(rangeLine, { size: 11, color: COLOR_MUTED })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [
        run(
          `${recordCount} record${recordCount === 1 ? "" : "s"} on file`,
          { size: 11, color: COLOR_MUTED },
        ),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 480 },
      children: [
        run(`Prepared ${formatLong(new Date().toISOString())}`, {
          size: 11,
          color: COLOR_MUTED,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        run("Generated by CaseThread", {
          size: 10,
          italics: true,
          color: COLOR_MUTED,
        }),
      ],
    }),
    new Paragraph({ children: [new PageBreak()] }),
  );
  return children;
}

function buildToc(): Paragraph[] {
  // Word's native TOC field — hyperlinked, auto-populated from heading
  // styles 1-2 on document open. updateFields: true on the Document
  // makes Word prompt the user to refresh it when the file opens.
  const toc = new TableOfContents("Contents", {
    hyperlink: true,
    headingStyleRange: "1-2",
  });
  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 0, after: 180 },
      children: [
        new TextRun({
          text: "Contents",
          font: FONT,
          bold: true,
          size: 32,
          color: COLOR_HEADING,
        }),
      ],
    }),
    // TS: TableOfContents is a valid section child; the Section
    // children array accepts Table/Paragraph/TableOfContents but the
    // inferred type is Paragraph here — the runtime accepts the cast.
    toc as unknown as Paragraph,
    new Paragraph({ children: [new PageBreak()] }),
  ];
}

function buildCaseOverview(overview: CaseOverview | null): Paragraph[] {
  const out: Paragraph[] = [heading1("Case overview")];
  if (!overview || overview.isEmpty) {
    out.push(
      para(
        "No facts have been extracted for this case yet. Run “Extract all facts” on the Overview tab to populate this section.",
        { italics: true, color: COLOR_MUTED },
      ),
    );
    return out;
  }
  if (overview.headerLine) {
    out.push(para(overview.headerLine, { color: COLOR_MUTED }));
  }
  out.push(para(overview.provenanceLegend, { italics: true, color: COLOR_MUTED }));
  const section = (title: string, lines: string[], bullet = false) => {
    if (lines.length === 0) return;
    out.push(heading2(title));
    for (const l of lines) out.push(para(bullet ? `• ${l}` : l));
  };
  section("Parties involved", overview.parties, true);
  section("Key case events", overview.keyEvents);
  section("Coverage terms", overview.coverageTerms, true);
  if (overview.financialGroups.length > 0) {
    out.push(heading2("Financial summary"));
    for (const g of overview.financialGroups) {
      out.push(para(g.header, { bold: true }));
      for (const l of g.lines) out.push(para(l));
    }
  }
  section("Commitments & deadlines", overview.commitments);
  return out;
}

function buildCurrentStatus(paragraph: string): Paragraph[] {
  return [heading1("Current status"), ...prose(paragraph)];
}

function buildChronologicalNarrative(
  narrative: string | null,
  records: CaseRecord[],
): Paragraph[] {
  const out: Paragraph[] = [heading1("Chronological narrative")];
  // Provenance [rec:ID] citations → "(Record #N)" text matching the
  // Appendix A numbering (sorted by date), so every claim carries a
  // visible source reference. Unresolved ids are dropped, never leaked.
  const sorted = [...records].sort((a, b) =>
    (a.record_date ?? "").localeCompare(b.record_date ?? ""),
  );
  const idxById = new Map<string, number>();
  sorted.forEach((r, i) => idxById.set(r.id, i + 1));
  const transform = (raw: string): string =>
    raw.replace(/\s*\[rec:([^\]\s]+)\]/g, (_m, id: string) => {
      const idx = idxById.get(id);
      return idx ? ` (Record #${idx})` : "";
    });
  const trimmed = transform(narrative ?? "").trim();
  if (trimmed) {
    // Blank lines separate paragraphs; render each run as its own
    // spaced paragraph so long stories don't fuse into a wall of
    // text. Word-editable — the user can restyle freely.
    for (const p of trimmed.split(/\n\s*\n/)) {
      const pTrim = p.trim();
      if (pTrim) out.push(...prose(pTrim));
    }
  } else {
    out.push(
      para(
        "Timeline not yet generated — open the Timeline tab and click Generate to produce a chronological narrative.",
        { italics: true, color: COLOR_MUTED },
      ),
    );
  }
  return out;
}

function buildPartiesInvolved(parties: PartySummary[]): Paragraph[] {
  const out: Paragraph[] = [heading1("Parties involved")];
  if (parties.length === 0) {
    out.push(
      para("No parties identified yet.", {
        italics: true,
        color: COLOR_MUTED,
      }),
    );
    return out;
  }
  const header = new TableRow({
    tableHeader: true,
    children: [
      cell("Name", { bold: true, width: 35, shaded: true }),
      cell("Role", { bold: true, width: 25, shaded: true }),
      cell("Email", { bold: true, width: 30, shaded: true }),
      cell("Mentions", { bold: true, width: 10, shaded: true }),
    ],
  });
  // Aliases render inline as "(also: X, Y)" so editors reviewing the
  // brief in Word can see the merge trail without a separate column.
  const rows = parties.map(
    (p) =>
      new TableRow({
        children: [
          cell(
            p.aliases.length > 0
              ? `${p.name} (also: ${p.aliases.join(", ")})`
              : p.name,
            { width: 35 },
          ),
          cell(p.role ?? "", { width: 25 }),
          cell(p.email ?? "", { width: 30 }),
          cell(String(p.mentions), { width: 10 }),
        ],
      }),
  );
  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [header, ...rows],
  });
  out.push(table as unknown as Paragraph);
  return out;
}

function buildDisclaimer(): Paragraph[] {
  return [
    heading1("About this document"),
    para(AI_DISCLAIMER, {
      italics: true,
      size: 10,
      color: COLOR_MUTED,
    }),
    para(copyrightLine(), { size: 9, color: COLOR_MUTED }),
  ];
}

function buildKeyFactsTable(facts: KeyFact[]): Paragraph[] {
  const out: Paragraph[] = [heading1("Key facts")];
  if (facts.length === 0) {
    out.push(
      para("No key facts extracted yet.", {
        italics: true,
        color: COLOR_MUTED,
      }),
    );
    return out;
  }
  const header = new TableRow({
    tableHeader: true,
    children: [
      cell("Type", { bold: true, width: 25, shaded: true }),
      cell("Fact", { bold: true, width: 75, shaded: true }),
    ],
  });
  const rows = facts.map(
    (f) =>
      new TableRow({
        children: [
          cell(KEY_FACT_TYPE_LABEL[f.type] ?? f.type, { width: 25 }),
          cell(f.fact, { width: 75 }),
        ],
      }),
  );
  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [header, ...rows],
  });
  out.push(table as unknown as Paragraph);
  return out;
}


function buildVolumeSummaries(
  volumeBriefs: VolumeBriefEntry[],
  records: CaseRecord[],
): Paragraph[] {
  const closed = volumeBriefs
    .filter((v) => v.volume.closed_at !== null)
    .sort((a, b) => a.volume.volume_number - b.volume.volume_number);
  if (closed.length === 0) return [];
  const out: Paragraph[] = [heading1("Volume summaries")];
  for (const entry of closed) {
    const { volume, summary, keyFacts } = entry;
    const title = volume.title?.trim()
      ? `Volume ${volume.volume_number} — ${volume.title.trim()}`
      : `Volume ${volume.volume_number}`;
    out.push(heading2(title));
    // Actual content span from record_dates (see helper comment).
    const { first, last } = volumeDateRange(records, volume.id);
    const range =
      first && last
        ? first === last
          ? formatLong(first)
          : `${formatLong(first)} to ${formatLong(last)}`
        : "Date range not available";
    out.push(
      para(`${range} · ${volume.record_count} records`, {
        size: 10,
        color: COLOR_MUTED,
      }),
    );
    if (summary?.trim()) {
      // Scrub cached "at 2:00:33 PM CDT" style timestamps from
      // narratives generated before the timezone fix.
      out.push(...prose(stripProseTimestamps(summary)));
    } else {
      out.push(
        para("No summary cached for this volume.", {
          italics: true,
          color: COLOR_MUTED,
        }),
      );
    }
    if (keyFacts.length > 0) {
      // Compact Type | Fact table, matching the main-body Key Facts
      // layout. Previously these rendered as a run-on sentence which
      // was unreadable past a few facts.
      out.push(
        new Paragraph({
          spacing: { before: 120, after: 60 },
          children: [run("Key facts", { bold: true, size: 11 })],
        }),
      );
      const header = new TableRow({
        tableHeader: true,
        children: [
          cell("Type", { bold: true, width: 25, shaded: true }),
          cell("Fact", { bold: true, width: 75, shaded: true }),
        ],
      });
      const rows = keyFacts.map(
        (f) =>
          new TableRow({
            children: [
              cell(KEY_FACT_TYPE_LABEL[f.type] ?? f.type, { width: 25 }),
              cell(f.fact, { width: 75 }),
            ],
          }),
      );
      const table = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [header, ...rows],
      });
      out.push(table as unknown as Paragraph);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export async function exportCaseAsDocx(bundle: ExportBundle): Promise<void> {
  const { case: caseData, records, contacts, volumes, volumeBriefs } = bundle;
  const keyFacts = parseKeyFacts(caseData);
  const parties = extractParties(records, contacts);
  const statusParagraph = buildStatusParagraph(
    caseData,
    records,
    volumeBriefs,
  );

  const coverChildren = buildCover(
    caseData.name,
    CASE_STATUS_LABELS[caseData.status] ?? caseData.status,
    dateRangeLine(records),
    records.length,
  );
  const tocChildren = buildToc();
  const summaryChildren = buildCaseOverview(bundle.overview ?? null);
  const statusChildren = buildCurrentStatus(statusParagraph);
  const keyFactsChildren = buildKeyFactsTable(keyFacts);
  const partiesChildren = buildPartiesInvolved(parties);
  // Prefer the bundle's pre-rendered narrative (built from the
  // timeline events via renderProseFromEvents) over `case.story`,
  // which is deprecated after the single-source-timeline
  // consolidation and no longer refreshed by the export flow.
  const narrativeChildren = buildChronologicalNarrative(
    bundle.narrative ?? caseData.story ?? null,
    records,
  );
  const volumeChildren =
    volumes.length > 1 ? buildVolumeSummaries(volumeBriefs, records) : [];
  const disclaimerChildren = buildDisclaimer();

  const pageHeader = new Header({
    children: [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          run(`CaseThread — ${caseData.name}`, {
            size: 9,
            color: COLOR_MUTED,
          }),
        ],
      }),
    ],
  });

  const pageFooter = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          run("Page ", { size: 9, color: COLOR_MUTED }),
          new TextRun({
            children: [PageNumber.CURRENT],
            font: FONT,
            size: 18,
            color: COLOR_MUTED,
          }),
          run(" of ", { size: 9, color: COLOR_MUTED }),
          new TextRun({
            children: [PageNumber.TOTAL_PAGES],
            font: FONT,
            size: 18,
            color: COLOR_MUTED,
          }),
        ],
      }),
    ],
  });

  const emptyHeader = new Header({
    children: [new Paragraph({ children: [] })],
  });

  const doc = new Document({
    creator: "CaseThread",
    title: `CaseThread — ${caseData.name} — Case brief`,
    // Tell Word to offer updating field content (including the TOC)
    // when the document is opened — otherwise the TOC renders empty
    // until the user manually updates it.
    features: { updateFields: true },
    styles: {
      default: {
        document: { run: { font: FONT, size: 22 } },
      },
      paragraphStyles: [
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          run: { font: FONT, bold: true, size: 32, color: COLOR_HEADING },
          paragraph: { spacing: { before: 360, after: 180 } },
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          run: { font: FONT, bold: true, size: 26, color: COLOR_HEADING },
          paragraph: { spacing: { before: 240, after: 120 } },
        },
      ],
    },
    sections: [
      {
        properties: {
          titlePage: true,
          page: {
            margin: { top: 720, right: 720, bottom: 720, left: 720 },
          },
        },
        headers: { first: emptyHeader, default: pageHeader },
        footers: { first: emptyHeader, default: pageFooter },
        children: [
          ...coverChildren,
          ...tocChildren,
          ...summaryChildren,
          ...statusChildren,
          ...keyFactsChildren,
          ...partiesChildren,
          ...narrativeChildren,
          ...volumeChildren,
          ...disclaimerChildren,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const dateSlug = new Date().toISOString().slice(0, 10);
  const filename = `CaseThread - ${slugify(caseData.name)} - brief - ${dateSlug}.docx`;
  triggerDownload(blob, filename);
}
